/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
// import { Spin } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
// import LayoutLogo from "../LayoutLogo.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import {
  getAppState, getAppJSON, setAppJSON, setAppState, changeAppState, modifyAGgridRows, getTheme, getCompact, getDesktopType, getUser, getLng, getDict,
  getSocketActions, clearSocketAction, getSafePtr
} from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
import uimin from "../../libs/uimin.tsx";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz, $ } = window;
const oneTimeLoad = {}, oneTimeLoading = {};
const renderState:{lastLNG:{[key:string]:string}, lastSocketKeys:string[]} = {lastLNG: {}, lastSocketKeys: []};

// const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
const LoadingSpinner = (loader:string) => (<div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}><div className={loader}></div></div>);

// let renderCounter = 0;

const Container:FC<TuniversalElementProps> = ({attributes, children}) => {
  Container.displayName = "Container";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const isCompact = useAppSelector(getCompact);
  const isMobile = useAppSelector(getDesktopType);
  const socketActions = useAppSelector(getSocketActions);
  const safe = useAppSelector(getSafePtr);

  const {rootAttributes, key, fullPath, align, overflow, height, width} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const {color, backgroundColor, borders, loader} = elState.attributes;
  const appJSON = useAppSelector(getAppJSON(rootAttributes.key));
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  // Global Grid-API to use it inside the clients-script:
  const changeState = (params) => {
    if (params && params.fullPath && params.newState) {
      dispatch(changeAppState({appkey: params.appkey, fullPath: params.fullPath, newState: params.newState}));
    }
  };

  // Global Grid-API to use it inside the clients-script:
  // ++++++++++++++++++++++++++++++++
  useEffect(() => {
    // @ts-ignore
    if (!window.changeState) { window.changeState = changeState }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootAttributes.key]);
  // ++++++++++++++++++++++++++++++++

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (!~userClss.indexOf("uk-container-expand")) { userClss.push("uk-container-expand") }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!~userClss.indexOf("rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    if (elState?.loading) {
      if (!~userClss.indexOf("uk-flex")) { userClss.push("uk-flex") }
      if (!~userClss.indexOf("uk-flex-middle")) { userClss.push("uk-flex-middle") }
      if (!~userClss.indexOf("uk-flex-center")) { userClss.push("uk-flex-center") }
    }
    else if (align) {
      if (!~userClss.indexOf("uk-flex")) { userClss.push("uk-flex") }
      (align || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt:string) => {
        if ((/^(left|center|right|between|around|stretch|top|middle|bottom|row|column|wrap|nowrap|first|last|none|auto|\d+)/igm).test(tt) && !~userClss.indexOf("uk-flex-" + tt)) {
          userClss.push("uk-flex-" + tt);
        }
      });
    }
    if (overflow && !rz.arrContains(userClss, /^uk-overflow-/igm)) {
      if ((/^(auto|hidden)$/igm).test(overflow)) {
        userClss.push("uk-overflow-" + overflow);
      }
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, elState?.loading, permissions.disabled, borders, align, overflow]);

  const toolbarHeight = useMemo(() => {
    let hght = 50;
    const tlb = $("#layout-main-toolbar"); if (tlb && tlb[0]) { hght = $(tlb[0]).height() }
    return hght;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, isCompact, isMobile]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor) {
      if (bclr) { est.backgroundColor = bclr }
      else { est.backgroundColor = (theme === "light" ? "#fff" : "#141414") }
    }
    if (height === "screen") {
      // @ts-ignore
      est.height = window.innerHeight - toolbarHeight; est.marginTop = toolbarHeight;
    }
    else if (height) { est.height = height }

    if (width) { est.width = width }
    return est;
  }, [elState.userStyle, elState?.visible, permissions.visible, color, backgroundColor, height, width, theme, toolbarHeight]);

  const loaderName = useMemo(() => {
    let ld = "loader02";
    if (loader) { ld = loader } else if (attributes?.loader) { ld = attributes.loader }
    return ld;
  }, [loader, attributes?.loader]);

  // Special function 'loading' - can change container content dynamically:
  // 2 attrs are required: loading="true" onloading="functionName" !!!
  useEffect(() => {
    if (oneTimeLoading[key] || !elState.loading || !attributes.jsonPath) { return } // !user?.login
    oneTimeLoading[key] = true; setTimeout(() => { oneTimeLoading[key] = false }, 1000);
    const fncName = attributes.onLoading || attributes.onloading;
    if (fncName && rootNamespace && rootNamespace[fncName]) {
      const containerJSONpath = attributes.jsonPath, containerJSON = rz.objByString(appJSON[0], containerJSONpath);
      // Debug:
      if (fnc.config?.isDev) console.log({action: "<Container key='" + key + "'> -> start loading content...", rootAttributes, attributes, appJSON, containerJSONpath, containerJSON});

      const clientFNC = rootNamespace[fncName];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, appJSON, containerJSONpath, containerJSON, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
          if (ret?.xml) {
            const newContainerJSON = uimin.ConvertSubXMLtoJSON(ret.xml);
            const firstPathElementOfContainerKey = attributes.fullPath.split("/")[0];
            let firstPathElementIndex = -1;
            for (let i = 0; i < appJSON.length; i++) {
              if (appJSON[i].type === "element" && appJSON[i].attributes && appJSON[i].attributes.key === firstPathElementOfContainerKey) { firstPathElementIndex = i; break }
            }
            if (newContainerJSON && ~firstPathElementIndex) {
              const newState = {...uimin.expandUIstate(appState, attributes.fullPath, newContainerJSON)};
              const newJSON = rz.objClone(appJSON), newJSON0 = rz.objClone(newJSON[firstPathElementIndex]);
              const result = rz.objModifyByString(newJSON0, (containerJSONpath.replace(/^\.elements\[\d+\]/igm, "")) + ".elements", newContainerJSON);
              newJSON[firstPathElementIndex] = newJSON0;
              // Debug-1: Именно эта строка поможет диагностировать, почему не найден "строковой путь" в JSON'е
              // console.log({result, newJSON0, stringPath: (containerJSONpath.replace(/^\.elements\[\d+\]/igm, "")) + ".elements", newContainerJSON});
              // Самая частая проблема - отсутствие коментария внутри <conteiner> (т.е. нет последнего '.elements'). ДОЛЖНО БЫТЬ ТАК:
              // <container key="cf-doc-constructor" overflow="auto" class="rz-theme-d5" loading="false" onLoading="updateXMLconstructor" >
              //   <!-- DYNAMICALLY BUILT CONTENT HERE -->
              // </container>

              // newState[attributes.fullPath].loading = false; // Так нельзя! newState is ReadOnly
              // Debug-2
              // eslint-disable-next-line max-len
              // console.log({action: "<Container key='" + key + "'> -> Applying new XML content", result, attributes, firstPathElementIndex, containerJSONpath, newJSON, newContainerElements: rz.objByString(newJSON0, (containerJSONpath.replace(/^\.elements\[\d+\]/igm, "")) + ".elements"), newContainerJSON, newState});
              if (result) {
                if (fnc.config?.isDev) console.log({action: "<Container key='" + key + "'> -> finish loading content", ret});
                // if (fnc.config?.isDev) console.log("++++++++++++++++ Container '" + key + "' has loaded a new content ++++++++++++++++");
                dispatch(setAppState({appkey: rootAttributes.key, newState: {...newState, [attributes.fullPath]: {...newState[attributes.fullPath], loading: false}}}));
                dispatch(setAppJSON({appkey: rootAttributes.key, newJSON}));
                if (ret?.state?.fullPath && ret?.state?.newState) {
                  setTimeout(() => {
                    dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
                  }, 500);
                }
              }
            }
          }
          else if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, appJSON, containerJSONpath, containerJSON, safe});
        if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
        if (ret?.xml) {
          const newContainerJSON = uimin.ConvertSubXMLtoJSON(ret.xml);
          const firstPathElementOfContainerKey = attributes.fullPath.split("/")[0];
          let firstPathElementIndex = -1;
          for (let i = 0; i < appJSON.length; i++) {
            if (appJSON[i].type === "element" && appJSON[i].attributes && appJSON[i].attributes.key === firstPathElementOfContainerKey) { firstPathElementIndex = i; break }
          }
          if (newContainerJSON && ~firstPathElementIndex) {
            const newState = {...uimin.expandUIstate(appState, attributes.fullPath, newContainerJSON)};
            const newJSON = rz.objClone(appJSON), newJSON0 = rz.objClone(newJSON[firstPathElementIndex]);
            const result = rz.objModifyByString(newJSON0, (containerJSONpath.replace(/^\.elements\[\d+\]/igm, "")) + ".elements", newContainerJSON);
            newJSON[firstPathElementIndex] = newJSON0;
            // newState[attributes.fullPath].loading = false; // Так нельзя! newState is ReadOnly
            // Debug
            // eslint-disable-next-line max-len
            // console.log({action: "<Container key='" + key + "'> -> Applying new XML content", result, attributes, firstPathElementIndex, containerJSONpath, newJSON, newContainerElements: rz.objByString(newJSON0, (containerJSONpath.replace(/^\.elements\[\d+\]/igm, "")) + ".elements"), newContainerJSON, newState});
            if (result) {
              if (fnc.config?.isDev) console.log({action: "<Container key='" + key + "'> -> finish loading content", ret});
              // if (fnc.config?.isDev) console.log("++++++++++++++++ Container '" + key + "' has loaded a new content ++++++++++++++++");
              dispatch(setAppState({appkey: rootAttributes.key, newState: {...newState, [attributes.fullPath]: {...newState[attributes.fullPath], loading: false}}}));
              dispatch(setAppJSON({appkey: rootAttributes.key, newJSON}));
              if (ret?.state?.fullPath && ret?.state?.newState) {
                setTimeout(() => {
                  dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
                }, 500);
              }
            }
          }
        }
        else if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [appJSON, appState, attributes, dispatch, elState, elState.loading, fullPath, key, rootAttributes.key, rootNamespace, user, user.login, lng, navigate, rootAttributes, dct, safe]);

  // Catch Any socket actions here!
  useEffect(() => {
    if (socketActions && socketActions.length) {
      const timeNow = (new Date()).getTime();
      const curScktActions = socketActions.filter((sa) => (((timeNow - sa.key) < 2000) && (!~renderState.lastSocketKeys.indexOf(rootAttributes.key + "_" + sa.key))));
      if (curScktActions.length) {
        const actn = (attributes?.onSocketAction || attributes?.onsocketaction || "");
        if (actn && rootNamespace && rootNamespace[actn]) {
          const clientFNC = rootNamespace[actn];
          // if (fnc.config?.isDev) console.log({action: "<Container key='" + key + "'> -> onSocketAction({" + rootAttributes.key + "})"});
          curScktActions.forEach((sckt) => {
            if ((!~renderState.lastSocketKeys.indexOf(rootAttributes.key + "_" + sckt.key))) {
              renderState.lastSocketKeys.push(rootAttributes.key + "_" + sckt.key);
              if (renderState.lastSocketKeys.length > 550) { renderState.lastSocketKeys.splice(0, 500) } // Memory saver
            }
            if (rz.isPromise(clientFNC)) {
              clientFNC({appkey: rootAttributes.key, socketAction: sckt, attributes, key, user, lng, dct, safe}).then((ret) => {
                if (ret) {
                  dispatch(clearSocketAction(sckt.key));
                }
              }).catch((err) => { console.log(err) });
            }
            else if (rz.isFunction(clientFNC)) {
              const ret = clientFNC({appkey: rootAttributes.key, socketAction: sckt, attributes, key, user, lng, dct, safe});
              if (ret) {
                dispatch(clearSocketAction(sckt.key));
              }
            }
          });
        }
      }
    }
  }, [socketActions, key, attributes, rootNamespace, user, lng, dispatch, rootAttributes.key, dct, safe]);

  // Container onLoad
  // Only 1 attr is required - onLoad="functionName" !!! But it can be run only one time !!!
  useEffect(() => {
    if (!oneTimeLoad[key]) {
      oneTimeLoad[key] = true;
      setTimeout(() => {
        const fncName = attributes.onload || attributes.onLoad;
        if (fncName && rootNamespace && rootNamespace[fncName]) {
          const clientFNC = rootNamespace[fncName];
          if (fnc.config?.isDev) console.log({action: "<Container key='" + key + "'> -> onLoad({" + rootAttributes.key + "})"});
          if (rz.isPromise(clientFNC)) {
            clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}).then((ret) => {
              if (rz.isUndefinedOrNull(ret)) { return }
              if (ret?.err) { console.log(ret.err) }
              if (ret?.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
              if (ret && ret?.state?.fullPath && ret?.state?.newState) {
                // const {ar} = store.getState(), apState = ar[rootAttributes.key];
                dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
              oneTimeLoad[key] = false;
            }).catch((err) => { console.log(err); oneTimeLoad[key] = false });
          }
          else if (rz.isFunction(clientFNC)) {
            const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe});
            if (rz.isUndefinedOrNull(ret)) { return }
            if (ret?.err) { console.log(ret.err) }
            if (ret?.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
            if (ret && ret?.state?.fullPath && ret?.state?.newState) {
              // const {ar} = store.getState(), apState = ar[rootAttributes.key];
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
            oneTimeLoad[key] = false;
          }
          else { oneTimeLoad[key] = false }
        }
        else { oneTimeLoad[key] = false }
      }, 1000);
    }
    // Reset 'loaded' state
    // return () => { oneTimeLoad = false };
  }, [key, attributes, rootNamespace, user, rootAttributes.key, lng, navigate, dct, dispatch, safe]);

  // ====================================================
  // Change LNG -> call User function to translate AGgrid content
  // ====================================================
  useEffect(() => {
    if (renderState.lastLNG[key] === lng) { return }
    // @ts-ignore
    renderState.lastLNG[key] = lng;

    const attr = (attributes?.onChangeLNG || attributes?.onchangelng || "");
    if (rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}).then((ret) => {
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe});
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [dct, lng, user, key, attributes, dispatch, rootAttributes.key, rootNamespace, safe]);

  // renderCounter++; console.log({renderCounter});
  // if (key === "ou-paths-loading") { console.log(elState) }

  return (
    <div key={key} id={key} className={clsu.join(" ")} style={styl}>
      {(elState?.loading ? LoadingSpinner(loaderName) : children)}
    </div>
  );
}

export default Container;
